/**
 * RF-07 · Calificación de indicadores
 * (`/api/evaluaciones-indicadores`).
 *
 * Registra la nota de UN evaluador sobre UN indicador en UNA ejecución. Tres
 * campos los calcula el backend y se ignoran si se envían: `calificacion_final`
 * (media reponderada sobre las dimensiones presentes), `id_grado` y
 * `nivel_asignado` (derivados del catálogo canónico).
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type EvaluacionIndicador from '@/interfaces/EvaluacionIndicador'
import type { EvaluacionIndicadorUpsertPayload } from '@/interfaces/EvaluacionIndicador'
import { http } from '@/services/http'

const BASE = '/api/evaluaciones-indicadores'

/**
 * Consulta evaluacion indicador · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns EvaluacionIndicador[]
 */
export async function getEvaluacionesIndicadorService(
  params: { id_version?: number; id_indicador?: number; id_usuario?: number; id_ejecucion?: number | null }
): Promise<EvaluacionIndicador[]> {
  const query = new URLSearchParams()
  if (params.id_version) query.set('id_version', String(params.id_version))
  if (params.id_indicador) query.set('id_indicador', String(params.id_indicador))
  if (params.id_usuario) query.set('id_usuario', String(params.id_usuario))
  if (params.id_ejecucion != null) query.set('id_ejecucion', String(params.id_ejecucion))
  const url = query.toString() ? `${BASE}?${query}` : BASE
  const result = await http.get<any>(url)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : d?.data ?? []
}

/**
 * Consulta evaluacion indicador · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns EvaluacionIndicador | null
 */
export async function getEvaluacionIndicadorByIdService(id: number): Promise<EvaluacionIndicador | null> {
  const result = await http.get<EvaluacionIndicador>(`${BASE}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea evaluacion indicador · petición `POST`.
 */
export async function crearEvaluacionIndicadorService(
  payload: EvaluacionIndicadorUpsertPayload
): Promise<{ ok: boolean; data?: EvaluacionIndicador; errors?: Record<string, string[]>; status?: number }> {
  const result = await http.post<EvaluacionIndicador>(BASE, payload)
  if (!result.ok) return { ok: false, errors: result.errors, status: result.status }
  return { ok: true, data: result.data, status: result.status }
}

/** Item de calificación dentro del guardado masivo. */
export interface EvaluacionBulkItem {
  id_indicador: number
  calificacion_documental?: number | null
  calificacion_cuantitativa?: number | null
  calificacion_opinion?: number | null
  observaciones?: string
  justificacion?: string
}

/** Forma con la que el API de evaluacion indicador devuelve este dato. */
export interface EvaluacionBulkPayload {
  id_version: number
  id_ejecucion?: number
  id_usuario: number
  fecha_evaluacion: string
  observaciones?: string
  justificacion?: string
  evaluaciones: EvaluacionBulkItem[]
}

/** Forma con la que el API de evaluacion indicador devuelve este dato. */
export interface EvaluacionBulkResultado {
  ok: boolean
  guardadas: number
  errores: { id_indicador: number; message: string }[]
  /** Mensaje global cuando la petición falla completa (422/500). */
  message?: string
}

/**
 * Guardado MASIVO: envía todas las calificaciones en UNA sola petición en lugar
 * de un request por indicador. El backend responde 207 si hubo guardado parcial.
 */
export async function guardarEvaluacionesBulkService(
  payload: EvaluacionBulkPayload
): Promise<EvaluacionBulkResultado> {
  const result = await http.post<{ guardadas: number; errores: { id_indicador: number; message: string }[] }>(
    `${BASE}/bulk`,
    payload
  )
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0]
      ?? (result.data as any)?.message
      ?? `Error del servidor (HTTP ${result.status})`
    return { ok: false, guardadas: 0, errores: [], message: msg }
  }
  const data = result.data
  return {
    ok: true,
    guardadas: data?.guardadas ?? 0,
    errores: data?.errores ?? []
  }
}

/**
 * Actualiza evaluacion indicador · petición `PUT`.
 */
export async function updateEvaluacionIndicadorService(
  id: number,
  payload: Record<string, unknown>
): Promise<{ ok: boolean; data?: EvaluacionIndicador; errors?: Record<string, string[]>; status?: number }> {
  const result = await http.put<EvaluacionIndicador>(`${BASE}/${id}`, payload)
  if (!result.ok) return { ok: false, errors: result.errors, status: result.status }
  return { ok: true, data: result.data, status: result.status }
}
